export const ACTIVE_SEASONS = {
  NFL: true,        // Toggle sports on/off
  NBA: true,        // 2026-27 regular season opens Tue Oct 20, 2026
  WNBA: false,      // 2026 season over: last game Oct 31, 2026; feed lists no events (2026-11-01)
  NHL: true,        // 2026-27 regular season opens Tue Sep 29, 2026
  MLB: false,       // 2026 season over: World Series ended Oct 28, 2026; feed lists no events (2026-11-01)
  NCAAF: true,
  MENSCBB: true     // 2026-27 season opened Sun Nov 1, 2026; ends with the title game Mon Apr 5, 2027 -> flip back to false
};

/**
 * Where each sport sits in its calendar. ACTIVE_SEASONS answers "do we have
 * boards to show"; this answers "what should we tell someone who arrived
 * anyway", which is a different question in the weeks before a season opens.
 *
 * - 'live'      boards are up; ACTIVE_SEASONS is true
 * - 'preseason' season opens shortly, no boards yet — say so rather than
 *               showing a dead-end "check back next season"
 * - 'off'       genuinely out of season
 *
 * Only consulted when ACTIVE_SEASONS[sport] is false, so 'live' here is
 * descriptive, never a second switch that could contradict the first.
 *
 * NOTHING EXPIRES THIS. A sport left on 'preseason' after its season ends will
 * cheerfully advertise "The <last season> Season Is Opening Soon" for the whole
 * real off-season, about a season that just finished. The phase has to move in
 * both directions:
 *
 *   season ends  -> set 'off'       (and ACTIVE_SEASONS false)
 *   ~weeks before opener -> 'preseason' + bump SEASON_LABELS
 *   boards live  -> 'live'          (and ACTIVE_SEASONS true)
 *
 * The 'off' -> 'preseason' move is the one someone remembers, because traffic
 * is arriving. The 'live' -> 'off' move is the one that gets forgotten, and it
 * is the one that ships a false statement.
 */
export const SEASON_PHASE = {
  NFL: 'live',
  NCAAF: 'live',
  WNBA: 'off',       // season over Oct 31 2026; 'preseason' + bump SEASON_LABELS a few weeks before the 2027 opener
  MLB: 'off',        // World Series ended Oct 28 2026; 'preseason' a few weeks before Opening Day 2027
  NBA: 'live',        // tip-off Oct 20 2026
  NHL: 'live',        // puck drop Sep 29 2026
  MENSCBB: 'live'    // opened Nov 1 2026; set 'off' after the Apr 5 2027 title game
};

// Display names for sports lists on marketing pages, in listing order:
// football first with NCAAF beside NFL, then everything else the config can
// switch on. [key, short name, long name].
const SPORT_DISPLAY_NAMES = [
  ['NFL', 'NFL', 'NFL'],
  ['NCAAF', 'NCAAF', 'college football'],
  ['MLB', 'MLB', 'MLB'],
  ['NHL', 'NHL', 'NHL'],
  ['NBA', 'NBA', 'NBA'],
  ['WNBA', 'WNBA', 'WNBA'],
  ['MENSCBB', 'NCAAB', 'men’s college basketball'],
];

/**
 * The sports in season, read from ACTIVE_SEASONS so a hardcoded list cannot
 * claim a sport that is off (#283). `long` gives prose names.
 */
export const activeSportNames = ({ long = false } = {}, seasons = ACTIVE_SEASONS) =>
  SPORT_DISPLAY_NAMES
    .filter(([key]) => seasons[key])
    .map(([, short, longName]) => (long ? longName : short));

/** ['NFL', 'NCAAF', 'MLB'] -> 'NFL, NCAAF and MLB'. */
export const joinNames = (names) => (names.length <= 1
  ? names.join('')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);
